import type { RenderChapterProgress, StopMode } from "@contract";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { errorCodeOf } from "../../../api/fetcher";
import { useJobRetry } from "../../../api/hooks";
import { queryKeys } from "../../../api/keys";
import { AudioProvider } from "../../../components/audio/AudioProvider";
import { Button } from "../../../components/control/Button";
import { Card } from "../../../components/data/Card";
import { DataTable } from "../../../components/data/DataTable";
import { Banner } from "../../../components/feedback/Banner";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { ProgressBar } from "../../../components/feedback/ProgressBar";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { DateTime } from "../../../components/format/DateTime";
import { Duration } from "../../../components/format/Duration";
import { Money } from "../../../components/format/Money";
import { Num } from "../../../components/format/Num";
import { WorkspaceActions } from "../../../components/layout/WorkspaceActions";
import { ConfirmDialog } from "../../../components/overlay/ConfirmDialog";
import { RespendConfirmDialog } from "../../../components/overlay/RespendConfirmDialog";
import { ChapterListen } from "../audio/ChapterListen";
import { useBookDetail } from "../bookContext";
import { useRenderProgress, useStopRender } from "../render/useRender";

function RetryRenderJob({ jobId }: { jobId: string }) {
  const { t } = useTranslation("progress");
  const book = useBookDetail();
  const retry = useJobRetry();
  const client = useQueryClient();
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="secondary"
        size="sm"
        busy={retry.isPending}
        onClick={() =>
          retry.mutate(jobId, {
            onSettled: () => {
              void client.invalidateQueries({ queryKey: queryKeys.renderProgress(book.id) });
            },
          })
        }
      >
        {t("retry")}
      </Button>
      <ErrorBanner error={retry.error} />
      <RespendConfirmDialog state={retry.respend} />
    </div>
  );
}

export function RenderProgressScreen() {
  const book = useBookDetail();
  return (
    <AudioProvider key={book.id}>
      <ProgressContent />
    </AudioProvider>
  );
}

function ProgressContent() {
  const { t } = useTranslation(["progress", "render", "errors"]);
  const book = useBookDetail();
  const query = useRenderProgress();
  const stop = useStopRender();
  const [mode, setMode] = useState<StopMode | null>(null);
  const data = query.data;
  if (!data && query.isPending) return <Skeleton variant="text" lines={8} />;
  if (!data)
    return (
      <ErrorState
        code={errorCodeOf(query.error)}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  const canStop = data.status === "rendering" || data.status === "stopping";
  return (
    <div className="flex flex-col gap-4" data-screen="progress">
      <ErrorBanner error={query.error ?? stop.error} />
      {data.stopRequested ? (
        <Banner tone="warn">
          {
            {
              afterChapter: t("progress:stopRequested.afterChapter"),
              immediately: t("progress:stopRequested.immediately"),
            }[data.stopRequested]
          }
        </Banner>
      ) : null}
      {data.status === "assembling" ? (
        <Banner tone="working">{t("progress:assembling")}</Banner>
      ) : null}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card title={t("progress:title")} padding="none" className="min-w-0">
          <div className="flex flex-col gap-3 px-6 pb-5">
            <div className="flex flex-wrap justify-between gap-3">
              <p>
                {
                  {
                    idle: t("progress:status.idle"),
                    rendering: t("progress:status.rendering"),
                    stopping: t("progress:status.stopping"),
                    assembling: t("progress:status.assembling"),
                    rendered: t("progress:status.rendered"),
                    failed: t("progress:status.failed"),
                    stopped: t("progress:status.stopped"),
                  }[data.status]
                }
              </p>
              <p className="font-mono text-label">
                {t("progress:completed", { count: data.chaptersDone, total: data.chaptersTotal })}
              </p>
            </div>
            <ProgressBar
              value={data.chaptersTotal ? data.chaptersDone / data.chaptersTotal : 0}
              label={t("progress:overall")}
            />
            {data.etaSeconds !== null ? (
              <p className="text-ink-2">
                {t("progress:eta")} <Duration ms={data.etaSeconds * 1000} style="words" approx />
              </p>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[840px]">
              <DataTable<RenderChapterProgress>
                rows={data.chapters}
                rowKey={(row) => row.chapterId}
                template="36px minmax(150px,1fr) minmax(160px,1fr) 95px 90px 160px"
                columns={[
                  {
                    id: "number",
                    header: t("render:number"),
                    cell: (row) => <Num value={row.number} />,
                  },
                  {
                    id: "title",
                    header: t("render:chapter"),
                    className: "whitespace-normal",
                    cell: (row) => (
                      <Link
                        className="hover:text-accent"
                        to={`/books/${book.id}/review/${row.chapterId}`}
                      >
                        {row.title}
                      </Link>
                    ),
                  },
                  {
                    id: "status",
                    header: t("progress:state"),
                    className: "whitespace-normal",
                    cell: (row) => (
                      <div className="flex flex-col gap-1">
                        <span
                          className={
                            row.status === "failed"
                              ? "text-danger"
                              : row.status === "rendered"
                                ? "text-ok"
                                : "text-ink-2"
                          }
                        >
                          {
                            {
                              queued: t("progress:chapterStatus.queued"),
                              rendering: t("progress:chapterStatus.rendering"),
                              rendered: t("progress:chapterStatus.rendered"),
                              retrying: t("progress:chapterStatus.retrying"),
                              failed: t("progress:chapterStatus.failed"),
                              stopped: t("progress:chapterStatus.stopped"),
                              skipped: t("progress:chapterStatus.skipped"),
                            }[row.status]
                          }
                        </span>
                        {row.progress ? (
                          <ProgressBar
                            value={row.progress.total ? row.progress.done / row.progress.total : 0}
                            label={t("progress:blocks", {
                              count: row.progress.done,
                              total: row.progress.total,
                            })}
                          />
                        ) : null}
                        {row.status === "retrying" ? (
                          <span className="text-warn">
                            {t("progress:attempt", { count: row.attempt, total: row.maxAttempts })}
                            {row.retryAt ? (
                              <>
                                {" "}
                                · <DateTime value={row.retryAt} style="dateTime" />
                              </>
                            ) : null}
                          </span>
                        ) : null}
                        {row.failure ? (
                          <span className="text-danger">{t(`errors:${row.failure.key}`)}</span>
                        ) : null}
                        {book.chapters.find((chapter) => chapter.id === row.chapterId)?.audio
                          ?.stale ? (
                          <span className="text-warn">{t("render:stale")}</span>
                        ) : null}
                      </div>
                    ),
                  },
                  {
                    id: "characters",
                    header: t("render:characters"),
                    cell: (row) => <Num value={row.charactersDone} />,
                  },
                  {
                    id: "spent",
                    header: t("progress:spent"),
                    cell: (row) => <Money cents={row.spent.cents} />,
                  },
                  {
                    id: "action",
                    header: t("render:audio"),
                    className: "whitespace-normal",
                    cell: (row) => (
                      <div className="flex flex-col items-start gap-1">
                        {row.renderedAt ? (
                          <ChapterListen
                            bookId={book.id}
                            chapterId={row.chapterId}
                            version={row.renderedAt}
                          />
                        ) : null}
                        {row.durationMs !== null ? <Duration ms={row.durationMs} /> : null}
                        {row.status === "failed" && row.jobId ? (
                          <RetryRenderJob jobId={row.jobId} />
                        ) : null}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
          <WorkspaceActions>
            <Link className="text-accent" to={`/books/${book.id}/render`}>
              {t("progress:backEstimate")}
            </Link>
            {canStop ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={data.stopRequested !== null}
                  onClick={() => setMode("afterChapter")}
                >
                  {t("progress:stopAfter")}
                </Button>
                <Button
                  variant="danger"
                  disabled={data.stopRequested === "immediately"}
                  onClick={() => setMode("immediately")}
                >
                  {t("progress:stopNow")}
                </Button>
              </div>
            ) : book.audiobook || data.status === "rendered" ? (
              <Link className="text-accent underline" to={`/books/${book.id}/audiobook`}>
                {t("progress:download")}
              </Link>
            ) : null}
          </WorkspaceActions>
        </Card>
        <aside className="flex flex-col gap-4">
          <Card label={t("progress:costs")}>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-4">
              <span />
              <span className="label-mono">{t("progress:spent")}</span>
              <span className="label-mono">{t("progress:estimated")}</span>
              <span>{t("render:characters")}</span>
              <Num value={data.spent.characters} />
              <Num value={data.estimated.characters} />
              <span>{t("render:credits")}</span>
              <Num value={data.spent.credits} />
              <Num value={data.estimated.credits} />
              <span>{t("render:amount")}</span>
              <Money cents={data.spent.money.cents} />
              <Money cents={data.estimated.money.cents} approx />
              <span>{t("render:audio")}</span>
              <Duration ms={data.spent.durationMs} />
              <Duration ms={data.estimated.durationMs} />
            </div>
          </Card>
          <Card label={t("progress:assembly")}>
            <p>
              {data.assembly
                ? {
                    queued: t("progress:assemblyStatus.queued"),
                    running: t("progress:assemblyStatus.running"),
                    succeeded: t("progress:assemblyStatus.succeeded"),
                    failed: t("progress:assemblyStatus.failed"),
                    cancelled: t("progress:assemblyStatus.cancelled"),
                  }[data.assembly.status]
                : t("progress:assemblyWaiting")}
            </p>
            {data.assembly?.failure ? (
              <p className="mt-2 text-danger">{t(`errors:${data.assembly.failure.key}`)}</p>
            ) : null}
            {data.assembly?.manualRetryRequired ? (
              <div className="mt-3">
                <RetryRenderJob jobId={data.assembly.id} />
              </div>
            ) : null}
          </Card>
          <p className="px-1 leading-relaxed text-ink-2">{t("progress:retryNote")}</p>
        </aside>
      </div>
      <ConfirmDialog
        open={mode !== null}
        title={mode === "immediately" ? t("progress:stopNow") : t("progress:stopAfter")}
        confirmLabel={t("progress:confirmStop")}
        tone="danger"
        busy={stop.isPending}
        onCancel={() => setMode(null)}
        onConfirm={() => {
          if (mode) stop.mutate(mode, { onSuccess: () => setMode(null) });
        }}
      >
        <p>{mode === "immediately" ? t("progress:stopNowBody") : t("progress:stopAfterBody")}</p>
        <ErrorBanner error={stop.error} />
      </ConfirmDialog>
    </div>
  );
}
